/**
 * "Who can watch" as plain choices (section 4) over the feed's entitlement (access + the products
 * that unlock it + a rental window). The choices:
 *
 *   free            everyone                         { access: "free" }
 *   members         members only                     { access: "subscription", products: memberships }
 *   members-or-buy  members, or anyone who rents/buys { access: "subscription", products: memberships + rent/buy }
 *   buy             only by renting or buying         { access: "rental" | "purchase", products: rent/buy }
 *
 * The products are what the brand sells (GET /admin/v1/products) plus what ticket codes and guest
 * lists unlock; product ids are picked, never typed. A product the item names that is neither is
 * kept as it is (it may be sold elsewhere). Pure, unit-tested.
 */

export type ProductKind = "subscription" | "purchase" | "rental";
export interface Product { productId: string; kind: ProductKind; rentalHours?: number; price?: string }

/** Something that unlocks an item without being sold in the app: a ticket-code batch or a guest list. */
export interface OtherUnlock { productId: string; label: string }

export interface Entitlement {
  access: "free" | "subscription" | "purchase" | "rental";
  products?: string[];
  rental?: { startWithinDays: number; watchWithinHours: number };
}

export type AccessChoice = "free" | "members" | "members-or-buy" | "buy";

export interface AccessView {
  choice: AccessChoice;
  /** Every product id the item names. */
  products: string[];
}

const isMembership = (id: string, products: Product[]) => products.find((p) => p.productId === id)?.kind === "subscription";

export function accessView(ent: Partial<Entitlement> | undefined, products: Product[]): AccessView {
  const ids = ent?.products ?? [];
  switch (ent?.access) {
    case "subscription": return { choice: ids.some((id) => !isMembership(id, products)) ? "members-or-buy" : "members", products: ids };
    case "purchase":
    case "rental": return { choice: "buy", products: ids };
    default: return { choice: "free", products: [] };
  }
}

/** What viewers can choose from under each choice: memberships, and things to rent, buy or unlock. */
export function offersFor(products: Product[], others: OtherUnlock[]): { memberships: Product[]; single: Array<Product | (OtherUnlock & { kind: "other" })> } {
  return {
    memberships: products.filter((p) => p.kind === "subscription"),
    single: [...products.filter((p) => p.kind !== "subscription"), ...others.filter((o) => !products.some((p) => p.productId === o.productId)).map((o) => ({ ...o, kind: "other" as const }))],
  };
}

/**
 * The entitlement for a choice and the products ticked. Switching to Members ticks every
 * membership when none is ticked yet (members means any membership, unless the editor narrows it).
 * A rental product brings a rental window: the one the item had, or the product's own hours.
 */
export function entitlementFor(choice: AccessChoice, ticked: string[], products: Product[], previous?: Partial<Entitlement>): Entitlement {
  if (choice === "free") return { access: "free" };
  const kindOf = (id: string): ProductKind | "other" => products.find((p) => p.productId === id)?.kind ?? "other";
  const memberships = products.filter((p) => p.kind === "subscription").map((p) => p.productId);
  let chosenMemberships = ticked.filter((id) => kindOf(id) === "subscription");
  if ((choice === "members" || choice === "members-or-buy") && chosenMemberships.length === 0) chosenMemberships = memberships;
  const single = choice === "members" ? [] : ticked.filter((id) => kindOf(id) !== "subscription");
  const ids = [...new Set(choice === "buy" ? single : [...chosenMemberships, ...single])];
  const rentals = single.filter((id) => kindOf(id) === "rental");
  const access: Entitlement["access"] = choice === "buy" ? (rentals.length ? "rental" : "purchase") : "subscription";
  const out: Entitlement = { access, ...(ids.length ? { products: ids } : {}) };
  if (rentals.length) {
    const hours = products.find((p) => p.productId === rentals[0])?.rentalHours;
    out.rental = previous?.rental ?? { startWithinDays: 30, watchWithinHours: hours && hours > 0 ? hours : 48 };
  }
  return out;
}

/** One line for an item's access, e.g. on a picker card: "Free", "Members", "Rent or buy". */
export function accessWords(ent: Partial<Entitlement> | undefined, products: Product[] = []): string {
  const view = accessView(ent, products);
  switch (view.choice) {
    case "free": return "Free";
    case "members": return "Members";
    case "members-or-buy": return "Members, or rent or buy";
    case "buy": return ent?.access === "rental" ? "Rent or buy" : "Buy";
  }
}

/** A product in words: "Monthly membership · €8.99", "Rent “Sintel” for 48 hours · €3.99". */
export function productLabel(p: { productId: string; kind: ProductKind; rentalHours?: number | null; price?: string | null }, brandId: string): string {
  const rest = p.productId.startsWith(`${brandId}.`) ? p.productId.slice(brandId.length + 1) : p.productId;
  const words = rest.split(/[._-]+/).filter(Boolean);
  const subject = (from: number) => words.slice(from).map((w) => w[0]!.toUpperCase() + w.slice(1)).join(" ");
  const price = p.price ? ` · ${p.price}` : "";
  if (p.kind === "subscription") {
    const period = /year|annual/.test(rest) ? "Yearly" : /month/.test(rest) ? "Monthly" : /week/.test(rest) ? "Weekly" : "";
    return `${period ? `${period} membership` : `Membership (${subject(0)})`}${price}`;
  }
  const what = words[0] === "rent" || words[0] === "buy" ? subject(1) : subject(0);
  if (p.kind === "rental") return `Rent “${what}”${p.rentalHours ? ` for ${p.rentalHours} hours` : ""}${price}`;
  return `Buy “${what}”${price}`;
}
